/**
 * Typed wrappers for outbound-webhook CRUD + test-fire (G17).
 *
 * Backend (handlers/outbound_webhooks.go):
 *   GET    /api/v1/outbound-webhooks               → { items: OutboundWebhook[] }
 *   POST   /api/v1/outbound-webhooks               → OutboundWebhook (201)
 *   GET    /api/v1/outbound-webhooks/{id}          → OutboundWebhook
 *   PATCH  /api/v1/outbound-webhooks/{id}          → OutboundWebhook
 *   DELETE /api/v1/outbound-webhooks/{id}          → 204
 *   POST   /api/v1/outbound-webhooks/{id}/test     → { status_code, error?, delivery_id? }
 *   GET    /api/v1/outbound-webhooks/{id}/deliveries → { items: Delivery[] }
 *   GET    /api/v1/outbound-webhooks/event-types   → { items: WebhookEventType[] }   (wave 2)
 *   POST   /api/v1/outbound-webhooks/{id}/rotate-secret → { webhook, signing_secret } (wave 2)
 *   GET    /api/v1/outbound-webhooks/{id}/dead-letters          → WebhookDeadLetters (generic)
 *   POST   /api/v1/outbound-webhooks/{id}/dead-letters/requeue  → { requeued, remaining }
 *   DELETE /api/v1/outbound-webhooks/{id}/dead-letters          → { discarded }
 *   GET    /api/v1/outbound-webhooks/{id}/deliveries?limit&cursor → one page + next_cursor (migration 132)
 *   POST   /api/v1/outbound-webhooks/{id}/deliveries/{rowId}/redeliver → RedeliverResult
 *   POST   /api/v1/outbound-webhooks/{id}/enable                → OutboundWebhook (back on, clean health)
 *   GET    /api/v1/outbound-webhooks/settings                   → WebhookSettings
 *   PUT    /api/v1/outbound-webhooks/settings                   → WebhookSettings
 *
 * Notes:
 *   - URL is returned MASKED ("https://host/[REDACTED]") — the plain URL is
 *     write-only; PATCH deliberately refuses a "url" key.
 *   - To change a URL, delete + re-create (backend-enforced).
 *   - All state-changing requests go through the shared axios instance
 *     (CSRF + cookie auth handled centrally).
 *
 * Migration note (Wave F, 2026-06-05): Vite SPA port of
 * frontend/src/lib/api/webhooks.ts. Same types and function signatures;
 * import path changed to Vite SPA's api/client.ts.
 */

import { api } from "./client";

export const WEBHOOKS_BASE = "/api/v1/outbound-webhooks";

/**
 * "generic" (wave 2) = any HTTPS endpoint that receives the signed JSON
 * event (HMAC-SHA256, X-WorkRight-Signature). Its signing secret is
 * returned ONCE — by create and by rotate-secret.
 */
export type WebhookProvider = "slack" | "teams" | "generic";

/** Fields returned by List / Get / Create / Update. URL is MASKED. */
export interface OutboundWebhook {
  id: number;
  provider: WebhookProvider;
  /** Slack/Teams: event type or "*". Generic: "*" or "subscribed" (server-derived). */
  category: string;
  /** Generic only: explicit subscription; [] = every non-confidential event. */
  event_types: string[];
  url_masked: string;
  label: string;
  is_active: boolean;
  last_used_at?: string | null;
  last_error?: string | null;
  last_status?: number | null;
  /** Generic only. */
  secret_rotated_at?: string | null;
  /** Failed delivery attempts in a row (reset by any 2xx). */
  consecutive_failures?: number;
  /** When the current failure streak started (absent when healthy). */
  failing_since?: string | null;
  /**
   * Set when WorkRight turned the webhook off after a long run of failures
   * (20 in a row over at least 24 h). Turning it back on clears it.
   */
  auto_disabled_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OutboundWebhookDelivery {
  id: number;
  notif_category: string;
  payload_summary: string;
  status_code?: number | null;
  error?: string | null;
  sent_at: string;
  /** Generic only — the X-WorkRight-Delivery id (same across retries). */
  delivery_id?: string | null;
  attempt?: number | null;
  duration_ms?: number | null;
  /** Set when this attempt failed and a retry is scheduled. */
  next_retry_at?: string | null;
  /** Generic only: the delivery failed for good and its payload is still held. */
  redeliverable?: boolean;
}

/** POST response — generic adds the one-time signing secret. */
export interface CreateWebhookResponse extends OutboundWebhook {
  signing_secret?: string;
}

export interface WebhookEventType {
  type: string;
  label: string;
  group: string;
  /** POSH notice: only delivered when selected explicitly. */
  confidential: boolean;
}

export interface RotateWebhookSecretResponse {
  webhook: OutboundWebhook;
  signing_secret: string;
  previous_valid_until?: string | null;
}

export interface ListWebhooksResponse {
  items: OutboundWebhook[];
}

export interface ListDeliveriesResponse {
  items: OutboundWebhookDelivery[];
  /** Pass as `cursor` for the next (older) page; null on the last page. */
  next_cursor?: string | null;
}

export interface CreateWebhookRequest {
  provider: WebhookProvider;
  /** Slack/Teams only. */
  category?: string;
  /** Generic only ([] / absent = all events). */
  event_types?: string[];
  url: string;
  label: string;
  is_active?: boolean;
}

export interface UpdateWebhookRequest {
  label?: string;
  /** Slack/Teams only. */
  category?: string;
  /** Generic only — replaces the subscription ([] = all events). */
  event_types?: string[];
  is_active?: boolean;
}

export interface TestWebhookResponse {
  status_code: number;
  error?: string;
  /** Generic only. */
  delivery_id?: string;
}

export async function listWebhooks(
  signal?: AbortSignal,
): Promise<ListWebhooksResponse> {
  const { data } = await api.get<ListWebhooksResponse>(WEBHOOKS_BASE, {
    signal,
  });
  return data;
}

export async function createWebhook(
  body: CreateWebhookRequest,
): Promise<CreateWebhookResponse> {
  const { data } = await api.post<CreateWebhookResponse>(WEBHOOKS_BASE, body);
  return data;
}

/** GET /event-types — the generic-webhook subscription catalog. */
export async function listWebhookEventTypes(
  signal?: AbortSignal,
): Promise<{ items: WebhookEventType[] }> {
  const { data } = await api.get<{ items: WebhookEventType[] }>(
    `${WEBHOOKS_BASE}/event-types`,
    { signal },
  );
  return data;
}

/**
 * POST /{id}/rotate-secret — generic only. The previous secret keeps
 * signing alongside the new one for overlapHours (0 = revoke now).
 */
export async function rotateWebhookSecret(
  id: number,
  overlapHours: number,
): Promise<RotateWebhookSecretResponse> {
  const { data } = await api.post<RotateWebhookSecretResponse>(
    `${WEBHOOKS_BASE}/${id}/rotate-secret`,
    { overlap_hours: overlapHours },
  );
  return data;
}

export async function updateWebhook(
  id: number,
  body: UpdateWebhookRequest,
): Promise<OutboundWebhook> {
  const { data } = await api.patch<OutboundWebhook>(
    `${WEBHOOKS_BASE}/${id}`,
    body,
  );
  return data;
}

export async function deleteWebhook(id: number): Promise<void> {
  await api.delete(`${WEBHOOKS_BASE}/${id}`);
}

export async function testWebhook(id: number): Promise<TestWebhookResponse> {
  const { data } = await api.post<TestWebhookResponse>(
    `${WEBHOOKS_BASE}/${id}/test`,
  );
  return data;
}

/** One page of the delivery log, newest first (cursor = previous next_cursor). */
export async function listWebhookDeliveries(
  id: number,
  signal?: AbortSignal,
  cursor?: string | null,
  limit = 50,
): Promise<ListDeliveriesResponse> {
  const { data } = await api.get<ListDeliveriesResponse>(
    `${WEBHOOKS_BASE}/${id}/deliveries`,
    { signal, params: { limit, ...(cursor ? { cursor } : {}) } },
  );
  return data;
}

export interface RedeliverResult {
  delivery_id: string;
  status_code: number;
  error?: string;
  /** The endpoint answered 2xx; the parked copy is gone. */
  delivered: boolean;
  /** Not delivered and still kept for another try. */
  parked: boolean;
}

/** Resend a parked delivery now (generic webhooks; row = a delivery-log row id). */
export async function redeliverWebhookDelivery(
  id: number,
  deliveryRowId: number,
): Promise<RedeliverResult> {
  const { data } = await api.post<RedeliverResult>(
    `${WEBHOOKS_BASE}/${id}/deliveries/${deliveryRowId}/redeliver`,
  );
  return data;
}

/** Turn a webhook back on with a clean health record. */
export async function enableWebhook(id: number): Promise<OutboundWebhook> {
  const { data } = await api.post<OutboundWebhook>(
    `${WEBHOOKS_BASE}/${id}/enable`,
  );
  return data;
}

export interface WebhookSettings {
  delivery_log_retention_days: number;
  /** false = the defaults (nothing saved yet). */
  saved: boolean;
  min_retention_days: number;
  max_retention_days: number;
  default_retention_days: number;
  dead_letter_retention_days: number;
  auto_disable_after_failures: number;
  auto_disable_min_streak_hours: number;
}

export async function getWebhookSettings(
  signal?: AbortSignal,
): Promise<WebhookSettings> {
  const { data } = await api.get<WebhookSettings>(`${WEBHOOKS_BASE}/settings`, {
    signal,
  });
  return data;
}

export async function updateWebhookSettings(body: {
  delivery_log_retention_days: number;
}): Promise<WebhookSettings> {
  const { data } = await api.put<WebhookSettings>(
    `${WEBHOOKS_BASE}/settings`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Dead letters (generic webhooks) — deliveries that failed for good and are
// kept (parked, never retried automatically) until an admin requeues,
// redelivers or discards them, or they expire.
// ---------------------------------------------------------------------------

/**
 * queue_full — the retry queue was at its cap; exhausted — every retry
 * failed; rejected — a non-retryable answer (e.g. HTTP 400/401);
 * endpoint_disabled — the webhook was turned off while it waited.
 */
export type WebhookDeadLetterReason =
  | "queue_full"
  | "exhausted"
  | "rejected"
  | "endpoint_disabled";

export interface WebhookDeadLetter {
  id: number;
  delivery_id: string;
  event_type: string;
  attempts: number;
  reason: WebhookDeadLetterReason;
  dead_lettered_at: string;
  created_at: string;
}

export interface WebhookDeadLetters {
  /** Live retries waiting for their next attempt. */
  queued: number;
  /** Parked deliveries (total; `items` is the oldest 100). */
  dead_letters: number;
  items: WebhookDeadLetter[];
  /** The live-queue cap per webhook. */
  queue_cap: number;
  /** Parked deliveries are dropped after this many days. */
  retention_days: number;
}

export async function listWebhookDeadLetters(
  id: number,
  signal?: AbortSignal,
): Promise<WebhookDeadLetters> {
  const { data } = await api.get<WebhookDeadLetters>(
    `${WEBHOOKS_BASE}/${id}/dead-letters`,
    { signal },
  );
  return data;
}

/** Requeue parked deliveries (up to the free room under the queue cap). */
export async function requeueWebhookDeadLetters(
  id: number,
): Promise<{ requeued: number; remaining: number }> {
  const { data } = await api.post<{ requeued: number; remaining: number }>(
    `${WEBHOOKS_BASE}/${id}/dead-letters/requeue`,
  );
  return data;
}

/** Discard every parked delivery (irreversible). */
export async function discardWebhookDeadLetters(
  id: number,
): Promise<{ discarded: number }> {
  const { data } = await api.delete<{ discarded: number }>(
    `${WEBHOOKS_BASE}/${id}/dead-letters`,
  );
  return data;
}

